import React, { useMemo, useRef, useCallback, useEffect } from 'react';
import { GoogleMap, Circle, Marker, Polyline, useJsApiLoader } from '@react-google-maps/api';
import { OPCIONES_GOOGLE_MAPS } from '../../mapas/opcionesGoogleMaps';

// Misma API key que el resto del proyecto (GoogleMapsEmpleado / GoogleMaps).
const CONTAINER = { width: '100%', height: 380, borderRadius: 8 };
const CENTER_EC = { lat: -1.5, lng: -78.6 };  // Ecuador (vista inicial antes del encuadre)

// Rampa verde -> naranja -> rojo por intensidad relativa.
const heatColor = (t) => (t >= 0.66 ? '#E23D3D' : t >= 0.33 ? '#F39C12' : '#2ECC71');
const num = (n) => (n == null ? 0 : Number(n)).toLocaleString('es-EC');

/**
 * Capa de calor de alarmas (geohash -> centroide + conteo) + marcadores de riesgo de
 * contaminacion. Google removio HeatmapLayer en Maps JS v3.65, asi que la densidad se
 * dibuja con circulos ponderados translucidos (el solape entre celdas simula el calor).
 * Encuadra automaticamente a los puntos con datos. Solo lectura.
 */
// Color de la alarma sobre el recorrido.
const alarmColor = (a) => (a.es_critico ? '#E23D3D' : a.familia === 'Candado' ? '#E08A08' : '#3B82C4');

const MapaCalorGps = ({ puntos, riesgos, foco, tramos, recorrido, eventos, totalEventos, onReady }) => {
    const { isLoaded } = useJsApiLoader(OPCIONES_GOOGLE_MAPS);
    const mapRef = useRef(null);
    const onLoad = useCallback((m) => { mapRef.current = m; }, []);
    const onUnmount = useCallback(() => { mapRef.current = null; }, []);

    useEffect(() => { if (isLoaded && onReady) onReady(); }, [isLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

    // Foco: cuando se hace clic en una zona/riesgo, centra y acerca a ese punto.
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !window.google || !foco || foco.lat == null || foco.lon == null) return;
        map.panTo({ lat: Number(foco.lat), lng: Number(foco.lon) });
        map.setZoom(15);
    }, [foco]);

    // Al seleccionar un sector, encuadra a sus tramos individuales.
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !window.google || !(tramos && tramos.length)) return;
        const pts = tramos.filter((t) => t.latitud != null && t.longitud != null)
            .map((t) => ({ lat: Number(t.latitud), lng: Number(t.longitud) }));
        if (!pts.length) return;
        if (pts.length === 1) { map.setCenter(pts[0]); map.setZoom(14); return; }
        const b = new window.google.maps.LatLngBounds();
        pts.forEach((p) => b.extend(p));
        map.fitBounds(b, 80);
    }, [tramos]);

    const circles = useMemo(() => {
        const pts = (puntos || []).filter((p) => p.lat != null && p.lon != null);
        const max = Math.max(1, ...pts.map((p) => Number(p.n) || 1));
        return pts.map((p) => {
            const t = (Number(p.n) || 1) / max;
            return {
                center: { lat: Number(p.lat), lng: Number(p.lon) },
                radius: 400 + 1100 * Math.sqrt(t),          // metros (geohash prec.6 ~1.2km)
                color: heatColor(t),
                opacity: 0.2 + 0.35 * t,
                z: Math.round(t * 100),
            };
        });
    }, [puntos]);

    const marks = useMemo(
        () => (riesgos || []).filter((r) => r.latitud != null && r.longitud != null),
        [riesgos]);

    // Recorrido: traza (polilinea) + alarmas del vehiculo seleccionado.
    const path = useMemo(
        () => ((recorrido && recorrido.track) || [])
            .filter((p) => p.lat != null && p.lon != null)
            .map((p) => ({ lat: Number(p.lat), lng: Number(p.lon) })),
        [recorrido]);
    const alarmas = (recorrido && recorrido.alarmas) || [];

    // Modo puntos: si el filtro deja pocas alarmas, se muestran marcadores individuales (ubicables).
    const modoPuntos = !recorrido && totalEventos != null && Number(totalEventos) <= 250 && (eventos || []).length > 0;
    const puntosEvt = useMemo(
        () => (modoPuntos ? (eventos || []).filter((e) => e.lat != null && e.lon != null) : []),
        [modoPuntos, eventos]);

    // Encuadra a los puntos individuales cuando se muestran.
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !window.google || !puntosEvt.length) return;
        if (puntosEvt.length === 1) { map.setCenter({ lat: Number(puntosEvt[0].lat), lng: Number(puntosEvt[0].lon) }); map.setZoom(14); return; }
        const b = new window.google.maps.LatLngBounds();
        puntosEvt.forEach((e) => b.extend({ lat: Number(e.lat), lng: Number(e.lon) }));
        map.fitBounds(b, 60);
    }, [puntosEvt]);

    // Al cargar un recorrido, encuadra a la traza.
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !window.google || !path.length) return;
        if (path.length === 1) { map.setCenter(path[0]); map.setZoom(14); return; }
        const b = new window.google.maps.LatLngBounds();
        path.forEach((p) => b.extend(p));
        map.fitBounds(b, 60);
    }, [path]);

    // Encuadre automatico a los puntos con datos (a zoom de pais los circulos serian invisibles).
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !window.google) return;
        const all = circles.map((c) => c.center)
            .concat(marks.map((r) => ({ lat: Number(r.latitud), lng: Number(r.longitud) })));
        if (!all.length) { map.setCenter(CENTER_EC); map.setZoom(6); return; }
        if (all.length === 1) { map.setCenter(all[0]); map.setZoom(12); return; }
        const b = new window.google.maps.LatLngBounds();
        all.forEach((p) => b.extend(p));
        map.fitBounds(b, 40);
    }, [circles, marks, isLoaded]);

    if (!isLoaded) {
        return <div style={{ height: 380, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8494A3', fontSize: 13 }}>Cargando mapa…</div>;
    }

    const vacio = !circles.length && !marks.length;

    return (
        <div style={{ position: 'relative' }}>
            <GoogleMap mapContainerStyle={CONTAINER} center={CENTER_EC} zoom={6}
                onLoad={onLoad} onUnmount={onUnmount}
                options={{
                    mapTypeControl: true,       // permite mapa / satelite / terreno / hibrido
                    streetViewControl: true,    // pegman (vista de calle)
                    fullscreenControl: true,
                    zoomControl: true,
                    mapTypeId: 'roadmap',
                }}>
                {!path.length && !modoPuntos && circles.map((c, i) => (
                    <Circle key={i} center={c.center} radius={c.radius}
                        options={{ strokeWeight: 0, fillColor: c.color, fillOpacity: c.opacity, clickable: false, zIndex: c.z }} />
                ))}
                {/* Marcadores individuales de alarma (filtro con pocas alarmas) */}
                {puntosEvt.map((e, i) => (
                    <Marker key={`ev${i}`} position={{ lat: Number(e.lat), lng: Number(e.lon) }}
                        icon={{
                            path: window.google.maps.SymbolPath.CIRCLE, scale: 6,
                            fillColor: alarmColor(e), fillOpacity: 1, strokeColor: '#fff', strokeWeight: 1.5,
                        }}
                        title={`${e.placa || ''} · ${e.evento || ''} · ${num(e.duracion_min)} min${e.en_zona_oscura ? ' · en zona oscura' : ''}`} />
                ))}
                {/* Recorrido real del vehiculo (traza + alarmas) */}
                {path.length > 1 && (
                    <Polyline path={path} options={{ strokeColor: '#3B57C4', strokeOpacity: 0.9, strokeWeight: 3, zIndex: 500 }} />
                )}
                {path.length > 0 && (
                    <Marker position={path[0]} label={{ text: 'A', color: '#fff', fontSize: '11px', fontWeight: 'bold' }}
                        title="Inicio del recorrido" />
                )}
                {path.length > 1 && (
                    <Marker position={path[path.length - 1]} label={{ text: 'B', color: '#fff', fontSize: '11px', fontWeight: 'bold' }}
                        title="Fin del recorrido" />
                )}
                {alarmas.filter((a) => a.lat != null && a.lon != null).map((a, i) => (
                    <Marker key={`al${i}`} position={{ lat: Number(a.lat), lng: Number(a.lon) }}
                        icon={{
                            path: window.google.maps.SymbolPath.CIRCLE, scale: 6,
                            fillColor: alarmColor(a), fillOpacity: 1, strokeColor: '#fff', strokeWeight: 1.5,
                        }}
                        title={`${a.evento || ''} · ${num(a.duracion_min)} min${a.en_zona_oscura ? ' · en zona oscura' : ''}`} />
                ))}
                {marks.map((r, i) => (
                    <Marker key={`m${i}`} position={{ lat: Number(r.latitud), lng: Number(r.longitud) }}
                        icon={{
                            path: window.google.maps.SymbolPath.CIRCLE, scale: 7,
                            fillColor: r.riesgo_contaminacion === 'ALTO' ? '#B91C1C' : '#E08A08',
                            fillOpacity: 1, strokeColor: '#fff', strokeWeight: 1.5,
                        }}
                        title={`${r.placa || 's/placa'} · riesgo ${r.riesgo_contaminacion} · ${num(r.minutos_sin_senal)} min sin señal`} />
                ))}
                {(tramos || []).filter((t) => t.latitud != null && t.longitud != null).map((t, i) => (
                    <Circle key={`tz${i}`} center={{ lat: Number(t.latitud), lng: Number(t.longitud) }}
                        radius={Math.min(4000, Math.max(400, (Number(t.minutos_sin_senal) || 10) * 40))}
                        options={{ strokeColor: '#E23D3D', strokeWeight: 1.5, fillColor: '#E23D3D', fillOpacity: 0.12, clickable: false, zIndex: 997 }} />
                ))}
                {(tramos || []).filter((t) => t.latitud != null && t.longitud != null).map((t, i) => (
                    <Marker key={`tm${i}`} position={{ lat: Number(t.latitud), lng: Number(t.longitud) }}
                        icon={{ path: window.google.maps.SymbolPath.CIRCLE, scale: 5, fillColor: '#E23D3D', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 1 }}
                        title={`${t.placa || ''} · ${num(t.minutos_sin_senal)} min sin señal`} />
                ))}
                {foco && foco.lat != null && foco.lon != null && foco.minutos != null && !(tramos && tramos.length) && (
                    <Circle center={{ lat: Number(foco.lat), lng: Number(foco.lon) }}
                        radius={Math.min(5000, Math.max(500, Number(foco.minutos) * 40))}
                        options={{ strokeColor: '#E23D3D', strokeWeight: 2, fillColor: '#E23D3D', fillOpacity: 0.12, clickable: false, zIndex: 998 }} />
                )}
                {foco && foco.lat != null && foco.lon != null && (
                    <Marker position={{ lat: Number(foco.lat), lng: Number(foco.lon) }}
                        icon={{
                            path: window.google.maps.SymbolPath.CIRCLE, scale: 12,
                            fillColor: '#0E9E97', fillOpacity: 0.25, strokeColor: '#0B7E7A', strokeWeight: 2.5,
                        }}
                        zIndex={999} title={foco.label || 'Seleccionado'} />
                )}
            </GoogleMap>
            {vacio && (
                <div style={{ position: 'absolute', top: 10, left: 10, background: 'rgba(255,255,255,.92)', border: '1px solid #DBE3EC', borderRadius: 8, padding: '8px 12px', fontSize: 12.5, color: '#566575' }}>
                    Sin puntos con GPS en el corte seleccionado. Generá un corte de muestra para ver la densidad.
                </div>
            )}
        </div>
    );
};

export default MapaCalorGps;
